/**
 * v4 Motion presets
 * ----------------------------------------------------------------------------
 * Typed framer-motion variants/transitions shared across every v4 surface.
 * Enter animations are 150–350ms, ease-out. Import what you need:
 *
 *   import { motion } from "framer-motion";
 *   import { fadeUp, stagger, staggerItem, viewportOnce, hoverLift } from "@/v4/theme/motion";
 *
 *   <motion.section variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
 *     <motion.div variants={staggerItem}>…</motion.div>
 *   </motion.section>
 *
 *   <motion.div {...fadeUp()} />               // one-shot in-view fade-up
 *   <motion.button {...hoverLift} />           // press/hover feedback
 *
 * Reduced motion: use `prefersReducedMotion()` (client-only) to swap in
 * `noMotion` when the user asked for less animation. framer also respects the
 * OS setting via <MotionConfig reducedMotion="user"> if you wrap the tree.
 */

import type { Variants, Transition, TargetAndTransition } from 'framer-motion';

/* ---- Shared easing/timing ------------------------------------------------ */

/** Signature ease-out curve used throughout the mockups. */
export const EASE_OUT = [0.22, 1, 0.36, 1] as const;

export const enterTransition: Transition = {
  duration: 0.35,
  ease: EASE_OUT,
};

/** In-view viewport config: animate once, trigger ~60px before entering. */
export const viewportOnce = { once: true, margin: '-60px' } as const;

/* ---- Variants ------------------------------------------------------------ */

/** Fade + rise. Use as a variant (hidden/show) or via the fadeUp() spread. */
export const fadeUpVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: enterTransition },
};

/** Plain fade, no translate. */
export const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.3, ease: EASE_OUT } },
};

/** Container that staggers its children in. Pair with staggerItem. */
export const stagger: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.04 },
  },
};

/** Child of a `stagger` container. */
export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: enterTransition },
};

/** Page-level enter/exit for route transitions (use with AnimatePresence). */
export const pageTransition: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE_OUT } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2, ease: 'easeIn' } },
};

/* ---- Spreadable prop bundles --------------------------------------------- */

/**
 * One-shot in-view fade-up. Spread onto a motion element:
 *   <motion.div {...fadeUp(0.1)} />
 */
export const fadeUp = (delay = 0) =>
  ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: viewportOnce,
    transition: { duration: 0.35, delay, ease: EASE_OUT },
  }) as const;

/** One-shot in-view fade (no translate). */
export const fadeIn = (delay = 0) =>
  ({
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    viewport: viewportOnce,
    transition: { duration: 0.3, delay, ease: EASE_OUT },
  }) as const;

/** Hover uplift + tap press. Spread onto interactive motion elements. */
export const hoverLift = {
  whileHover: { y: -3, transition: { duration: 0.2, ease: EASE_OUT } } as TargetAndTransition,
  whileTap: { scale: 0.98 } as TargetAndTransition,
};

/** Subtle press-only feedback for buttons. */
export const pressScale = {
  whileHover: { scale: 1.015 } as TargetAndTransition,
  whileTap: { scale: 0.99 } as TargetAndTransition,
};

/* ---- Reduced motion helpers ---------------------------------------------- */

/** Static "no-op" props — swap these in when reduced motion is preferred. */
export const noMotion = {
  initial: false as const,
  animate: undefined,
  whileInView: undefined,
  whileHover: undefined,
  whileTap: undefined,
  transition: { duration: 0 },
};

/**
 * Client-only check for the user's reduced-motion preference.
 * Returns false during SSR. Usage:
 *   const reduce = prefersReducedMotion();
 *   <motion.div {...(reduce ? noMotion : fadeUp())} />
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
